import { Application, Container } from 'pixi.js';
import { addResizeListener } from './display';
import { Game } from './game';
import { theme } from 'game/theme';
import { CanvasRenderer } from '@pixi/canvas-renderer';
import { extensions } from '@pixi/core';
import { Store } from 'game/store/gameStore';

// Simple FPS Counter
class FPSCounter {
    private fpsElement: HTMLDivElement;
    private frames: number = 0;
    private lastTime: number = performance.now();
    private fps: number = 0;

    constructor() {
        this.fpsElement = document.createElement('div');
        this.fpsElement.style.position = 'absolute';
        this.fpsElement.style.top = '0';
        this.fpsElement.style.left = '0';
        this.fpsElement.style.padding = '8px';
        this.fpsElement.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        this.fpsElement.style.color = '#00ff00';
        this.fpsElement.style.fontFamily = 'monospace';
        this.fpsElement.style.fontSize = '14px';
        this.fpsElement.style.zIndex = '10000';
        this.fpsElement.style.borderRadius = '4px';
        this.fpsElement.style.userSelect = 'none';
        this.fpsElement.textContent = 'FPS: 0';
    }

    appendTo(container: HTMLElement) {
        container.appendChild(this.fpsElement);
    }

    update() {
        this.frames++;
        const currentTime = performance.now();
        const elapsed = currentTime - this.lastTime;

        if (elapsed >= 1000) {
            this.fps = Math.round((this.frames * 1000) / elapsed);
            this.fpsElement.textContent = `FPS: ${this.fps}`;
            
            // Color code based on FPS
            if (this.fps >= 50) {
                this.fpsElement.style.color = '#00ff00'; // Green
            } else if (this.fps >= 30) {
                this.fpsElement.style.color = '#ffff00'; // Yellow
            } else {
                this.fpsElement.style.color = '#ff0000'; // Red
            }
            
            this.frames = 0;
            this.lastTime = currentTime;
        }
    }
}

// Register the canvas renderer
export type AppSetting = {
    backgroundColor?: number;
    backgroundAlpha?: number;
};
const defaultAppSettings = {
    backgroundColor: theme.gameBackground,
    backgroundAlpha: 1,
} as AppSetting;
class PixiRenderer {
    app: Application;
    stageContainer: Container;
    fpsCounter: FPSCounter | null = null;

    constructor() {
        this.onResize = this.onResize.bind(this);
        this.render = this.render.bind(this);
        this.stageContainer = new Container();
        try {
            extensions.add(CanvasRenderer);
        } catch (error) {
            console.error('Error adding canvas renderer', error);
        }
        this.app = new Application();
    }
    async initApp(option: AppSetting) {
        const settings = { ...defaultAppSettings, ...option };
        const isLowEnd = Store.lowPerformanceDevice;
        
        
        await this.app.init({
            resolution: window.devicePixelRatio,
            autoDensity: true, // Let PixiJS handle CSS scaling automatically
            antialias: false,
            
            // Performance optimizations
            useBackBuffer: true,
            clearBeforeRender: isLowEnd,
            preserveDrawingBuffer: !isLowEnd,
            
            // Rendering optimizations
            hello: false,
            
            // Visual settings
            background: settings.backgroundColor,
            backgroundAlpha: settings.backgroundAlpha,
        });

        // Additional ticker optimization for low-end devices
        if (isLowEnd) {
            this.app.ticker.maxFPS = 30;
        }

        this.startApp();
    }
    startApp() {
        const gameContainer = document.getElementById('solitaire-container');
        if (!gameContainer) {
            setTimeout(() => {
                this.startApp();
            }, 100);
            return;
        }
        
        // Prevent adding canvas multiple times
        if (!gameContainer.contains(this.app.canvas)) {
            gameContainer.appendChild(this.app.canvas);
        }
        
        this.app.stage.addChild(this.stageContainer);
        
        // // Add FPS counter to top-left corner
        // this.fpsCounter = new FPSCounter();
        // this.fpsCounter.appendTo(gameContainer);
        
        // // Update FPS counter on every frame
        // this.app.ticker.add(() => {
        //     if (this.fpsCounter) {
        //         this.fpsCounter.update();
        //     }
        // });
        
        addResizeListener(this.onResize, 'rendererResize');
        Game.start();
        this.app.canvas.classList.add('loaded');
        // setTimeout(() => {
        //     this.app.canvas.classList.add('loaded');
        // const { gl } = this.app.renderer.context;
        // if (!gl) {
        //     console.error('WebGL context is not available');
        //     return;
        // }
        // gl.canvas.addEventListener('webglcontextlost', () => {
        //     console.error('WebGl context lost - reload a page');
        //     window.location.reload();
        // });
        // }, 10);
    }
    onResize(width: number, height: number) {
        this.app.renderer.resize(width, height);
    }
    render() {
        this.app.renderer.render(this.app.stage);
    }
}

export const App = new PixiRenderer();

class GameTime {
    get deltaTime() {
        return App.app ? App.app.ticker.deltaTime : 1;
    }
    get deltaTimeMS() {
        return App.app ? App.app.ticker.deltaMS : 1;
    }
}
export const Time = new GameTime();
